import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { buildProjectName } from '../utils/projectName';

const ProjectContext = createContext(null);

const DEFAULT_PROJECT = {
  name: 'Virus VS Bacteria',
  category: 'cosmetics',
  aspectRatio: '9:16',
  targetDuration: 30,
  tone: 'Thực tế, dễ chốt, phân tích ưu nhược điểm rõ ràng',
  audience: 'Phân biệt sự khác nhau giữa Virus và Bacteria',
  voiceId: 'voice-vi-nu-hoat-ngon',
  avatarId: 'avatar-custom-mascot',
  avatarMode: 'static', // 'static' | 'four_poses'
  avatarUrl: '/mascos_test.png',
  poses: {
    neutral: '/mascos_test.png',
    pointLeft: '',
    pointRight: '',
    attention: ''
  },
  backgroundId: 'bg-pharmacy-gpp',
  subjectA: {
    name: 'Virus',
    brand: 'Virus là các hạt protein và acid nucleic.',
    description: 'Kích thước hiển vi, có thể nhân lên trong tế bào sống.',
    features: ['Phải ký sinh tế bào sống để nhân lên.', 'Thường có cấu trúc protein bao bọc acid nucleic.'],
    imageUrl: ''
  },
  subjectB: {
    name: 'Bacteria',
    brand: 'Vi khuẩn là các sinh vật đơn bào.',
    description: 'Thường có thành tế bào, sinh sản bằng cách phân đôi.',
    features: ['Có thể sống trong nhiều môi trường, kể cả không cần tế bào chủ để tồn tại.', 'Sinh sản độc lập, có cấu trúc tế bào hoàn chỉnh.'],
    imageUrl: ''
  },
  script: {
    fullText: `Bạn đang phân vân giữa lựa chọn Virus và Bacteria? Đây là bản rút gọn để chốt nhanh.

Với Virus, điểm mạnh là Virus có cấu trúc đơn giản.
Trong khi đó Bacteria nổi bật ở tốc độ, sự gọn nhẹ và giá trị trên chi phí bỏ ra.

Nếu bạn ưu tiên Virus hãy chọn Virus.
Nếu bạn cần Bacteria chọn Bacteria.

Chốt lại: chọn đúng theo nhu cầu, đừng chọn theo cảm tính.`,
    hook: {
      title: 'Mở đầu (Hook)',
      narration: 'Bạn đang phân vân giữa lựa chọn Virus và Bacteria? Đây là bản rút gọn để chốt nhanh.',
      durationSec: 4
    },
    sections: [
      {
        order: 1,
        title: 'Đặc Điểm & Công Dụng Phương Án A',
        narration: 'Với A, điểm mạnh là Virus có cấu trúc đơn giản.',
        durationSec: 8,
        subjectFocus: 'SUBJECT_A'
      },
      {
        order: 2,
        title: 'Đặc Điểm & Hiệu Quả Phương Án B',
        narration: 'Trong khi đó Bacteria nổi bật ở tốc độ, sự gọn nhẹ và giá trị trên chi phí bỏ ra.',
        durationSec: 8,
        subjectFocus: 'SUBJECT_B'
      }
    ],
    conclusion: {
      title: 'Tổng kết & Lời Khuyên',
      narration: 'Nếu bạn ưu tiên Virus hãy chọn Virus, nếu cần Bacteria chọn Bacteria.',
      durationSec: 6
    },
    cta: {
      title: 'Kêu Gọi Hành Động',
      narration: 'Chốt lại: chọn đúng theo nhu cầu, đừng chọn theo cảm tính.',
      durationSec: 4
    }
  },
  status: 'DRAFT'
};

// Dự án trắng: dùng khi bấm 'Tạo video mới' — không kèm ảnh/nội dung mẫu
const EMPTY_PROJECT = {
  name: 'Video So Sánh Mới',
  category: 'cosmetics',
  aspectRatio: '9:16',
  targetDuration: 30,
  tone: DEFAULT_PROJECT.tone,
  audience: '',
  voiceId: DEFAULT_PROJECT.voiceId,
  avatarId: DEFAULT_PROJECT.avatarId,
  avatarMode: 'static',
  avatarUrl: '',
  poses: { neutral: '', pointLeft: '', pointRight: '', attention: '' },
  backgroundId: DEFAULT_PROJECT.backgroundId,
  subjectA: { name: '', brand: '', description: '', features: [], imageUrl: '' },
  subjectB: { name: '', brand: '', description: '', features: [], imageUrl: '' },
  script: {
    fullText: '',
    hook: { title: 'Mở đầu (Hook)', narration: '', durationSec: 4 },
    sections: [],
    conclusion: { title: 'Tổng kết & Lời Khuyên', narration: '', durationSec: 6 },
    cta: { title: 'Kêu Gọi Hành Động', narration: '', durationSec: 4 }
  },
  status: 'DRAFT'
};

function createEmptyProject() {
  return JSON.parse(JSON.stringify(EMPTY_PROJECT));
}

// Bỏ các trường null/undefined do API trả về để không ghi đè cấu hình mặc định
function pruneEmpty(obj) {
  const out = {};
  Object.entries(obj || {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined) out[key] = value;
  });
  return out;
}

function buildSubjectPayload(subject = {}) {
  return {
    name: (subject.name || '').trim(),
    brand: subject.brand || '',
    description: subject.description || '',
    features: (subject.features || []).filter(f => f && f.trim()),
    imageUrl: subject.imageUrl || '',
    imageAssetId: subject.imageAssetId || null
  };
}

// Payload đúng schema backend (POST /projects, PATCH /projects/:id)
function buildProjectPayload(project) {
  return pruneEmpty({
    // Tên dự án suy ra từ A/B chứ không gửi nguyên văn ô nhập, xem utils/projectName.js
    name: buildProjectName(project.subjectA, project.subjectB),
    category: project.category || 'general',
    aspectRatio: project.aspectRatio || '9:16',
    targetDuration: project.targetDuration || 30,
    tone: project.tone || undefined,
    audience: project.audience || undefined,
    voiceId: project.voiceId || undefined,
    avatarId: project.avatarId || undefined,
    backgroundId: project.backgroundId || undefined,
    // Ảnh mascot/host để backend render đúng nhân vật người dùng đã tải lên
    avatarUrl: project.avatarUrl || '',
    avatarMode: project.avatarMode || 'static',
    poses: {
      neutral: project.poses?.neutral || '',
      pointLeft: project.poses?.pointLeft || '',
      pointRight: project.poses?.pointRight || '',
      attention: project.poses?.attention || ''
    },
    subjectA: buildSubjectPayload(project.subjectA),
    subjectB: buildSubjectPayload(project.subjectB)
  });
}

export function ProjectProvider({ children }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [credits, setCredits] = useState(124);
  const [project, setProject] = useState(DEFAULT_PROJECT);
  const [catalogs, setCatalogs] = useState({ voices: [], avatars: [], backgrounds: [], templates: [] });
  const [savedProjects, setSavedProjects] = useState([]);

  // Modals
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);

  // Loading States
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(true);
  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);
  const [isStartingRender, setIsStartingRender] = useState(false);
  const [isGeneratingPoses, setIsGeneratingPoses] = useState(false);
  const [isCancellingJob, setIsCancellingJob] = useState(false);

  // Render Job Tracking
  const [currentJob, setCurrentJob] = useState(null);
  const pollTimerRef = useRef(null);

  // Audio Playback
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [playingVoiceId, setPlayingVoiceId] = useState(null);
  const audioRef = useRef(new Audio());

  // Toast
  const [toast, setToast] = useState({ visible: false, message: '', type: 'info' });

  const showToast = (message, type = 'info') => {
    setToast({ visible: true, message, type });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 4000);
  };

  // Initial Load: Catalogs and Saved Projects
  useEffect(() => {
    async function init() {
      try {
        setIsLoadingCatalogs(true);
        const [catalogsData, projectsData] = await Promise.all([
          api.getCatalogs(),
          api.getProjects()
        ]);
        setCatalogs(catalogsData || {});
        setSavedProjects(projectsData || []);

        if (projectsData && projectsData.length > 0) {
          const firstProj = pruneEmpty(projectsData[0]);
          setProject(prev => ({
            ...prev,
            ...firstProj,
            subjectA: firstProj.subjectA || prev.subjectA,
            subjectB: firstProj.subjectB || prev.subjectB
          }));
          if (firstProj.latestRenderJobId) {
            api.getRenderJob(firstProj.latestRenderJobId).then(setCurrentJob).catch(() => { });
          }
        }
      } catch (err) {
        console.error('Failed to initialize app data:', err);
      } finally {
        setIsLoadingCatalogs(false);
      }
    }
    init();
  }, []);

  // Poll active render job
  useEffect(() => {
    if (currentJob && (currentJob.status === 'QUEUED' || currentJob.status === 'PROCESSING')) {
      pollTimerRef.current = setInterval(async () => {
        try {
          const updatedJob = await api.getRenderJob(currentJob.id);
          setCurrentJob(updatedJob);

          if (updatedJob.status === 'COMPLETED') {
            clearInterval(pollTimerRef.current);
            setCredits(prev => Math.max(0, prev - 12));
            showToast('Render video MP4 thành công! Video chỉ lưu trong 3 ngày, vui lòng tải về máy ngay.', 'success');
            api.getProjects().then(setSavedProjects);
          } else if (updatedJob.status === 'FAILED') {
            clearInterval(pollTimerRef.current);
            showToast(`Render thất bại: ${updatedJob.errorMessage || 'Lỗi không xác định'}`, 'danger');
          }
        } catch (err) {
          console.error('Error polling render job:', err);
        }
      }, 2000);
    }

    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJob?.id, currentJob?.status]);

  // Project state updates
  const updateProject = (updates) => {
    setProject(prev => ({ ...prev, ...updates }));
  };

  // Upload Asset (Image A or Image B)
  const handleUploadAsset = async (file, type) => {
    try {
      const asset = await api.uploadAsset(file, type);

      if (type === 'IMAGE_A') {
        updateProject({
          subjectA: {
            ...project.subjectA,
            imageUrl: asset.fileUrl,
            imageAssetId: asset.id
          }
        });
      } else if (type === 'IMAGE_B') {
        updateProject({
          subjectB: {
            ...project.subjectB,
            imageUrl: asset.fileUrl,
            imageAssetId: asset.id
          }
        });
      }
      showToast('Tải ảnh minh họa lên thành công!', 'success');
    } catch (err) {
      showToast(err.message || 'Lỗi khi tải ảnh lên', 'danger');
    }
  };

  const handleRemoveAsset = (subjectKey) => {
    updateProject({
      [subjectKey]: {
        ...project[subjectKey],
        imageUrl: '',
        imageAssetId: null
      }
    });
  };

  // Static Avatar Upload
  const handleUploadStaticAvatar = async (file) => {
    try {
      const asset = await api.uploadAsset(file, 'AVATAR');
      updateProject({ avatarUrl: asset.fileUrl });
      showToast('Tải ảnh mascot thành công!', 'success');
    } catch (err) {
      showToast('Lỗi khi tải mascot', 'danger');
    }
  };

  // 4 Poses Upload
  const handleUploadPose = async (poseKey, file) => {
    try {
      const asset = await api.uploadAsset(file, `POSE_${poseKey.toUpperCase()}`);
      updateProject({
        poses: {
          ...(project.poses || {}),
          [poseKey]: asset.fileUrl
        },
        avatarUrl: poseKey === 'neutral' ? asset.fileUrl : (project.avatarUrl || asset.fileUrl)
      });
      showToast(`Tải ${poseKey} thành công!`, 'success');
    } catch (err) {
      showToast('Lỗi khi tải ảnh pose', 'danger');
    }
  };

  const handleRemovePose = (poseKey) => {
    updateProject({
      poses: {
        ...(project.poses || {}),
        [poseKey]: ''
      }
    });
  };

  // Voice Preview
  const handlePreviewVoice = async (voice) => {
    try {
      // Nếu đang phát chính giọng này thì bấm lần nữa để dừng
      if (playingVoiceId === voice.id && audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setPlayingVoiceId(null);
        return;
      }

      setPlayingVoiceId(voice.id);

      // Luôn tổng hợp bằng chính adapter TTS dùng khi render (Gemini + cùng
      // logic chọn giọng resolveVoice/pickGeminiVoice) để giọng nghe thử
      // giống 100% giọng sẽ xuất hiện trong video - không phát file mp3 mẫu
      // dựng sẵn tách biệt, vì file đó không đảm bảo cùng giọng thực tế dùng
      // khi render và từng gây ra tình trạng "giọng video khác giọng mẫu".
      const sampleText = voice.sampleText || `Xin chào! Tôi là ${voice.name}. Tôi sẽ đọc lời bình cho video so sánh này.`;
      const res = await api.previewTTS({ text: sampleText, voiceId: voice.id });
      if (res && res.fileUrl) {
        audioRef.current.src = res.fileUrl;
        audioRef.current.onended = () => setPlayingVoiceId(null);
        audioRef.current.onerror = () => setPlayingVoiceId(null);
        await audioRef.current.play();
      } else {
        setPlayingVoiceId(null);
      }
    } catch (err) {
      setPlayingVoiceId(null);
      showToast('Không thể phát âm thanh nghe thử', 'warning');
    }
  };

  // Lưu dữ liệu form hiện tại lên server (tạo mới hoặc cập nhật) trước khi gọi AI/render,
  // để backend luôn dùng đúng nội dung chủ đề A/B đang nhập trên màn hình.
  const persistProject = async (proj) => {
    const payload = buildProjectPayload(proj);
    const saved = proj.id
      ? await api.updateProject(proj.id, payload)
      : await api.createProject(payload);
    // Tên do server suy ra từ cặp đối tượng nên phải lấy lại: chỉ giữ mỗi `id`
    // thì sửa đối tượng A/B xong, state và thẻ bên sidebar vẫn kẹt ở tên cũ.
    const merged = {
      ...proj,
      id: (saved && saved.id) || proj.id,
      name: (saved && saved.name) || proj.name
    };
    setProject(merged);
    setSavedProjects(prev => {
      const idx = prev.findIndex(p => p.id === merged.id);
      if (idx === -1) return saved ? [{ ...saved }, ...prev] : prev;
      const next = [...prev];
      next[idx] = { ...next[idx], ...(saved || {}), name: merged.name };
      return next;
    });
    return merged;
  };

  // Generate AI Script
  const handleGenerateScript = async (customOptions = {}) => {
    if (!project.subjectA?.name?.trim() || !project.subjectB?.name?.trim()) {
      showToast('Vui lòng nhập Chủ đề / sản phẩm A và B trước khi sinh kịch bản', 'warning');
      return;
    }
    try {
      setIsGeneratingScript(true);
      const activeProj = await persistProject(project);
      const res = await api.generateScript(activeProj.id, {
        tone: activeProj.tone,
        targetDuration: customOptions.targetDuration || activeProj.targetDuration || 30,
        provider: customOptions.provider || 'gemini',
        ...customOptions
      });
      updateProject({
        script: res.script,
        id: activeProj.id,
        targetDuration: customOptions.targetDuration || activeProj.targetDuration || 30
      });
      showToast(`Đã tạo kịch bản AI (${res.script?.generatedBy || 'AI'}) thành công!`, 'success');
    } catch (err) {
      showToast(err.message || 'Lỗi khi sinh kịch bản', 'danger');
    } finally {
      setIsGeneratingScript(false);
    }
  };

  // Generate TTS Audio
  const handleGenerateScriptAudio = async () => {
    try {
      setIsGeneratingAudio(true);
      const activeProj = await persistProject(project);
      const res = await api.generateScriptAudio(activeProj.id);
      if (res && res.fileUrl) {
        setAudioUrl(res.fileUrl);
        audioRef.current.src = res.fileUrl;
        audioRef.current.play();
        setIsPlayingAudio(true);
        audioRef.current.onended = () => setIsPlayingAudio(false);
        showToast('Đang phát giọng đọc kịch bản hoàn chỉnh!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Lỗi khi tổng hợp audio', 'danger');
    } finally {
      setIsGeneratingAudio(false);
    }
  };

  const handleToggleAudio = () => {
    if (!audioRef.current.src) {
      handleGenerateScriptAudio();
      return;
    }
    if (isPlayingAudio) {
      audioRef.current.pause();
      setIsPlayingAudio(false);
    } else {
      audioRef.current.play();
      setIsPlayingAudio(true);
      audioRef.current.onended = () => setIsPlayingAudio(false);
    }
  };

  // Nhờ AI vẽ 3 pose (chỉ trái / chỉ phải / giơ tay) từ chính ảnh mascot đã tải lên
  const handleGenerateAvatarPoses = async () => {
    if (!project.avatarUrl && !project.poses?.neutral) {
      showToast('Hãy tải ảnh Host/Mascot lên trước khi nhờ AI tạo pose', 'warning');
      return;
    }
    try {
      setIsGeneratingPoses(true);
      const activeProj = await persistProject(project);
      const updated = await api.generateAvatarPoses(activeProj.id);
      updateProject({
        poses: updated.poses || {},
        avatarMode: 'four_poses'
      });
      showToast('AI đã tạo xong bộ pose mascot!', 'success');
    } catch (err) {
      showToast(err.message || 'Không tạo được pose bằng AI', 'danger');
    } finally {
      setIsGeneratingPoses(false);
    }
  };

  // Step 3: Trigger Video Render Job
  const handleStartRender = async () => {
    try {
      setIsStartingRender(true);
      const activeProj = await persistProject(project);

      const job = await api.createRenderJob(activeProj.id, {
        quality: '1080p',
        includeSubtitles: true,
        includeAvatar: true
      });
      setCurrentJob(job);
      showToast('Đã khởi tạo tiến trình render video thật!', 'info');
    } catch (err) {
      showToast(err.message || 'Lỗi khi kích hoạt render video', 'danger');
    } finally {
      setIsStartingRender(false);
    }
  };

  const handleCancelRender = async (jobId) => {
    try {
      setIsCancellingJob(true);
      const res = await api.cancelRenderJob(jobId);
      setCurrentJob(res);
      showToast('Đã gửi yêu cầu hủy render', 'info');
    } catch (err) {
      showToast(err.message || 'Lỗi khi hủy render', 'danger');
    } finally {
      setIsCancellingJob(false);
    }
  };

  const handleSelectProject = async (id) => {
    try {
      const fullProj = await api.getProject(id);
      // Giữ giá trị mặc định cho các trường server trả về null (backgroundId, voiceId...)
      setProject({ ...createEmptyProject(), ...pruneEmpty(fullProj) });

      if (fullProj.latestRenderJobId) {
        try {
          const job = await api.getRenderJob(fullProj.latestRenderJobId);
          setCurrentJob(job);
        } catch (jobErr) {
          console.warn('Could not load render job for project:', jobErr.message);
          setCurrentJob(null);
        }
      } else {
        setCurrentJob(null);
      }

      showToast(`Đã mở dự án: ${fullProj.name}`, 'success');
    } catch (err) {
      showToast('Không thể tải dự án', 'danger');
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await api.deleteProject(id);
      setSavedProjects(prev => prev.filter(p => p.id !== id));
      if (project.id === id) {
        setProject(createEmptyProject());
      }
      showToast('Đã xóa dự án thành công', 'success');
    } catch (err) {
      showToast('Không thể xóa dự án', 'danger');
    }
  };

  const handleNewProject = () => {
    setProject(createEmptyProject());
    setCurrentJob(null);
    setAudioUrl(null);
    showToast('Đã tạo không gian làm việc cho video mới', 'info');
  };

  const handleAddCredits = (amount) => {
    setCredits(prev => prev + amount);
    showToast(`Nạp thành công +${amount} credit!`, 'success');
  };

  return (
    <ProjectContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        credits,
        project,
        updateProject,
        catalogs,
        savedProjects,
        isTemplatesModalOpen,
        setIsTemplatesModalOpen,
        isLoadingCatalogs,
        isGeneratingScript,
        isGeneratingAudio,
        isStartingRender,
        isCancellingJob,
        currentJob,
        audioUrl,
        isPlayingAudio,
        playingVoiceId,
        handlePreviewVoice,
        handleGenerateScript,
        handleGenerateScriptAudio,
        handleToggleAudio,
        handleUploadAsset,
        handleRemoveAsset,
        handleUploadStaticAvatar,
        handleUploadPose,
        handleRemovePose,
        isGeneratingPoses,
        handleGenerateAvatarPoses,
        handleStartRender,
        handleCancelRender,
        handleSelectProject,
        handleDeleteProject,
        handleNewProject,
        handleAddCredits,
        toast
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  if (!context) throw new Error('useProject must be used within a ProjectProvider');
  return context;
}

export default ProjectContext;
